<template>
    <div class="filters-bar" :class="{ 'filters-bar--mobile': $vuetify.breakpoint.mobile }">
        <div class="filters-bar__inner">
            <div class="filters-field filters-field--season">
                <v-select
                    v-model="season"
                    color="orange"
                    item-color="orange"
                    label="Σεζόν"
                    :items="seasons"
                    item-text="name"
                    return-object
                    dense
                    outlined
                    hide-details
                    prepend-inner-icon="mdi-calendar"
                />
            </div>

            <div class="filters-field filters-field--league">
                <v-select
                    v-model="league"
                    color="orange"
                    item-color="orange"
                    :items="leagueItems"
                    label="Κατηγορία"
                    item-text="title"
                    return-object
                    item-value="id"
                    :clearable="$route.name !== 'Standings'"
                    dense
                    outlined
                    hide-details
                    prepend-inner-icon="mdi-basketball"
                >
                    <template v-slot:item="{ item }">
                        <v-icon v-if="item === 'Cup'" small color="orange" class="mr-2">mdi-trophy</v-icon>
                        {{ item === 'Cup' ? 'Κύπελλο' : item.title }}
                    </template>
                    <template v-slot:selection="{ item }">
                        <v-icon v-if="item === 'Cup'" small color="orange" class="mr-2">mdi-trophy</v-icon>
                        {{ item === 'Cup' ? 'Κύπελλο' : item.title }}
                    </template>
                </v-select>
            </div>

            <div v-if="phaseOptions.length" class="filters-field filters-field--phase">
                <v-btn-toggle
                    v-model="phase"
                    mandatory
                    class="phase-toggle"
                    @change="league ? getRankingByLeague() : null"
                >
                    <v-btn
                        v-for="option in phaseOptions"
                        :key="option.value"
                        :value="option.value"
                        small
                        class="phase-toggle__btn"
                    >
                        {{ option.title }}
                    </v-btn>
                </v-btn-toggle>
            </div>

            <template v-if="$route.name === 'Schedule'">
                <div class="filters-field filters-field--round">
                    <div class="round-stepper" :class="{ 'round-stepper--disabled': !league }">
                        <v-btn icon small :disabled="!league || !round" @click="stepRound(-1)">
                            <v-icon>mdi-chevron-left</v-icon>
                        </v-btn>
                        <span class="round-stepper__label">
                            {{ round ? `Αγωνιστική ${round}` : 'Όλες οι αγωνιστικές' }}
                        </span>
                        <v-btn icon small :disabled="!league" @click="stepRound(1)">
                            <v-icon>mdi-chevron-right</v-icon>
                        </v-btn>
                    </div>
                </div>

                <div class="filters-field filters-field--arena">
                    <v-select
                        v-model="arena"
                        color="orange"
                        item-color="orange"
                        label="Γήπεδο"
                        :items="arenas"
                        item-text="name"
                        item-value="id"
                        return-object
                        clearable
                        dense
                        outlined
                        hide-details
                        prepend-inner-icon="mdi-map-marker"
                    />
                </div>
            </template>
        </div>
    </div>
</template>

<script>
    import seasonService from '@/services/season.js'
    import leagueService from '@/services/league.js'
    import arenaService from '@/services/arena'
    import moment from 'moment'
    import { availablePhases, pickPhase } from '@/utils/phases'

    const PHASE_OPTIONS = [
        { title: 'Προκριματικά', value: 'placements' },
        { title: 'Regular Season', value: 'regular_season' },
        { title: 'Playoffs', value: 'playoffs' }
    ]

    export default {
        props: {
            cup: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                season: null,
                league: null,
                arena: null,
                round: null,
                seasons: [],
                leagues: [],
                teams: [],
                filter: {},
                arenas: [],
                phase: 'regular_season',
                activePhasePresetApplied: false,
                // what the season has to show (null: unknown, all the options), and the season it is of
                seasonPhases: null,
                seasonPhasesId: null,
                lastRequestKey: null,
                routeFilterState: {
                    seasonId: null,
                    leagueId: null,
                    arenaId: null,
                    round: null,
                    phase: null,
                    isCup: false
                }
            }
        },
        computed: {
            activePhase() {
                const config = this.$store.getters.getConfig
                return config && config.activePhase ? config.activePhase : null
            },
            // only the phases of the league with matches (the playoffs also with a bracket)
            phaseOptions() {
                return availablePhases(PHASE_OPTIONS, this.seasonPhases, this.league)
            },
            hasCup() {
                return this.cup && (!this.seasonPhases || this.seasonPhases.cup)
            },
            leagueItems() {
                return this.hasCup ? this.leagues.concat('Cup') : this.leagues
            }
        },
        watch: {
            // '$route'(to, from) {
            //     if (to !== from) {
            //         new Promise((resolve) => { resolve(this.init()) }).then(() => {
            //             this.getByFilters()
            //         })
            //     }
            // },
            season: {
                handler(value, previous) {
                    // another season: what is on screen is not of it any more
                    if (previous) {
                        this.$emit('clear')
                        // its leagues come with the season
                        this.leagues = []
                        this.league = null
                        // the league of the link was of the season before
                        this.routeFilterState = { ...this.routeFilterState, leagueId: null, isCup: false }
                    }
                    if (value) {
                        this.$store.dispatch('setLeagueFilter', null)
                        this.$store.dispatch('setSeasonFilter', value)
                        this.filter = null
                        this.fetchSeasonPhases(value)
                        this.fetchAllLeagues()
                    }
                    this.getByFilters()
                }
            },
            phase: {
                handler() {
                    this.getByFilters()
                }
            },
            league: {
                handler(value) {
                    if (value) {
                        this.$store.dispatch('setLeagueFilter', value)
                        this.filter = null
                    }
                    this.getByFilters()
                }
            },
            arena: {
                handler(value) {
                    if (value) {
                        this.$store.dispatch('setLeagueFilter', null)
                        this.filter = null
                    }
                    this.getByFilters()
                }
            },
            round: {
                handler(value) {
                    if (value) {
                        this.$store.dispatch('setLeagueFilter', null)
                        this.filter = null
                    }
                    this.getByFilters()
                }
            },
            '$route.query': {
                handler() {
                    if (this.isPersistentFilterRoute()) {
                        this.readRouteFilters()
                    }
                }
            },
            activePhase() {
                this.applyPhasePreset()
            },
            phaseOptions() {
                this.fixPhase()
            }
        },
        created() {
            if (this.isPersistentFilterRoute()) {
                this.readRouteFilters()
            }
            this.applyPhasePreset()
            this.init()
        },
        methods: {
            stepRound(step) {
                const current = Number.parseInt(this.round, 10) || 0
                const next = current + step
                this.round = next > 0 ? next : null
            },
            applyPhasePreset(preferredPhase = null) {
                const hasPreferredPhase = !!preferredPhase
                const validPreferredPhase = hasPreferredPhase && PHASE_OPTIONS.some(option => option.value === preferredPhase)

                if (validPreferredPhase) {
                    this.phase = preferredPhase
                    this.activePhasePresetApplied = true
                    return
                }

                if (!this.activePhasePresetApplied && this.activePhase && (this.phase === null || this.phase === 'regular_season')) {
                    this.phase = this.activePhase
                    this.activePhasePresetApplied = true
                    return
                }

                if (!PHASE_OPTIONS.some(option => option.value === this.phase)) {
                    this.phase = 'regular_season'
                }
            },
            fetchSeasonPhases(season) {
                this.seasonPhasesId = null
                const done = (phases) => {
                    if (!this.season || this.season.id !== season.id) return
                    this.seasonPhases = phases
                    this.seasonPhasesId = season.id
                    this.fixPhase()
                    this.getByFilters()
                }
                seasonService.getPhases(season.id).then(resp => done(resp.data || null)).catch(() => done(null))
            },
            // a phase (or the cup) with nothing to show is not an option: on to one that has
            fixPhase() {
                if (this.league === 'Cup' && !this.hasCup) {
                    this.league = this.leagues[0] || null
                }
                this.phase = pickPhase(this.phase, this.phaseOptions, this.activePhase)
            },
            isPersistentFilterRoute() {
                return this.$route.name === 'Schedule' || this.$route.name === 'Standings'
            },
            readRouteFilters() {
                const seasonId = Number.parseInt(this.$route.query.seasonId, 10)
                const leagueId = Number.parseInt(this.$route.query.leagueId, 10)
                const arenaId = Number.parseInt(this.$route.query.arenaId, 10)
                const hasCupQuery = this.$route.query.isCup === 'true'
                const parsedPhase = this.$route.query.phase

                this.routeFilterState = {
                    seasonId: Number.isNaN(seasonId) ? null : seasonId,
                    leagueId: Number.isNaN(leagueId) ? null : leagueId,
                    arenaId: Number.isNaN(arenaId) ? null : arenaId,
                    round: this.$route.query.round || null,
                    phase: parsedPhase || null,
                    isCup: hasCupQuery
                }

                this.round = this.routeFilterState.round
                this.applyPhasePreset(this.routeFilterState.phase)
            },
            syncFiltersToRoute() {
                if (!this.isPersistentFilterRoute()) {
                    return
                }
                const nextQuery = {
                    ...this.$route.query
                }

                if (this.season && this.season.id !== undefined) {
                    nextQuery.seasonId = String(this.season.id)
                } else {
                    delete nextQuery.seasonId
                }

                if (this.league === 'Cup') {
                    nextQuery.isCup = 'true'
                    delete nextQuery.leagueId
                } else {
                    delete nextQuery.isCup
                    if (this.league && this.league.id !== undefined) {
                        nextQuery.leagueId = String(this.league.id)
                    } else {
                        delete nextQuery.leagueId
                    }
                }

                if (this.arena && this.arena.id !== undefined) {
                    nextQuery.arenaId = String(this.arena.id)
                } else {
                    delete nextQuery.arenaId
                }

                if (this.round !== null && this.round !== undefined && this.round !== '') {
                    nextQuery.round = String(this.round)
                } else {
                    delete nextQuery.round
                }

                // the cup has no phases
                if (this.phase && this.league !== 'Cup') {
                    nextQuery.phase = this.phase
                } else {
                    delete nextQuery.phase
                }

                const currentQuery = JSON.stringify(this.$route.query)
                const updatedQuery = JSON.stringify(nextQuery)
                if (currentQuery !== updatedQuery) {
                    this.$router.replace({ query: nextQuery }).catch(() => {})
                }
            },
            init() {
                this.fetchAllSeasons()
                this.fetchAllArenas()
                if (this.$store.getters.getSeasonFilter) {
                    this.season = this.$store.getters.getSeasonFilter
                }
                if (this.$store.getters.getLeagueFilter) {
                    this.league = this.$store.getters.getLeagueFilter
                    // if (this.$route.name === 'Standings') {
                    //     this.getRankingByLeague()
                    // }
                }
            },
            getRankingByLeague() {
                if (this.league === 'Cup') {
                    this.$emit('getCupState', true, null, this.season.id)
                } else if (this.phase === 'playoffs') {
                    this.$emit('getCupState', false, this.league.id, this.season.id)
                } else {
                    if (this.$route.name === 'Standings') {
                        this.$emit('getRankingByLeague', this.league.id, this.season.id, this.phase)
                    } else if (this.$route.name === 'Schedule') {
                        this.$emit('getScheduleByLeague', this.round, this.arena ? this.arena.id : null, this.league === 'Cup', this.league ? this.league.id : null, this.season ? this.season.id : null, this.phase ? this.phase : null)
                    }
                }
            },
            getRequestKey() {
                return JSON.stringify({
                    route: this.$route.name,
                    seasonId: this.season ? this.season.id : null,
                    leagueId: this.league === 'Cup' ? 'Cup' : (this.league ? this.league.id : null),
                    arenaId: this.arena ? this.arena.id : null,
                    round: this.round,
                    phase: this.phase
                })
            },
            getByFilters() {
                const nextRequestKey = this.getRequestKey()
                if (this.lastRequestKey === nextRequestKey) {
                    return
                }

                // the phases of the season first: the phase may not be an option
                if (!this.season || this.seasonPhasesId !== this.season.id) {
                    return
                }

                if (this.$route.name === 'Standings') {
                    if (this.season !== null && this.arenas.length > 0 && (this.leagues.length > 0 || this.league === 'Cup')) {
                        this.lastRequestKey = nextRequestKey
                        this.syncFiltersToRoute()
                        this.getRankingByLeague()
                    }
                } else {
                    if (this.season !== null && this.arenas.length > 0 && (this.leagues.length > 0 || this.league === 'Cup')) {
                        this.lastRequestKey = nextRequestKey
                        this.syncFiltersToRoute()
                        this.$emit('getByFilters', this.round, this.arena ? this.arena.id : null, this.league === 'Cup', this.league ? this.league.id : null, this.season ? this.season.id : null, this.phase ? this.phase : null)
                    }
                }
            },
            fetchAllSeasons() {
                seasonService.getSeasons(1, 100).then(resp => {
                    this.seasons = resp.data.content
                    if (this.routeFilterState.seasonId !== null) {
                        this.season = this.seasons.find(element => element.id === this.routeFilterState.seasonId) || null
                    }
                    if (!this.season) {
                        this.season = resp.data.content.filter(element => moment().isBetween(moment(element.startDate), moment(element.endDate)))[0]
                    }
                })
            },
            fetchAllLeagues() {
                // the leagues of the season in any phase: the phase is picked after the league
                const season = this.season
                leagueService.getLeagues(1, 100, season, null).then(resp => {
                    if (this.season !== season) return
                    this.leagues = resp.data.sort((a, b) => a.title.localeCompare(b.title))
                    if (this.routeFilterState.isCup && this.cup) {
                        this.league = 'Cup'
                    } else if (this.routeFilterState.leagueId !== null) {
                        this.league = this.leagues.find(element => element.id === this.routeFilterState.leagueId) || this.leagues[0] || null
                    } else {
                        this.league = this.$store.getters.getLeagueFilter ? this.$store.getters.getLeagueFilter : (this.leagues[0] || (this.hasCup ? 'Cup' : null))
                    }
                    this.getByFilters()
                })
            },
            fetchAllArenas() {
                arenaService.getArenas(1, 100).then(resp => {
                    this.arenas = resp.data.content
                    if (this.routeFilterState.arenaId !== null) {
                        this.arena = this.arenas.find(element => element.id === this.routeFilterState.arenaId) || null
                    }
                    this.getByFilters()
                })
            }
        }
    }
</script>

<style scoped>
/* top padding: same gap below the navbar as the other pages */
.filters-bar {
    padding: 12px 12px 0;
    margin-bottom: 20px;
}

.filters-bar__inner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 12px 16px;
    max-width: 1200px;
    margin: 0 auto;
    padding: 14px 18px;
    background: #fff;
    border-radius: 12px;
    border-top: 4px solid #ff7133;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
}

.filters-field--season {
    flex: 0 0 330px;
}

.filters-field--league {
    flex: 1 1 260px;
    max-width: 360px;
}

.filters-field--arena {
    flex: 1 1 220px;
    max-width: 320px;
}

/* ---------- Phase toggle ---------- */
.phase-toggle {
    background: #0d0d0f !important;
    border-radius: 8px !important;
    padding: 3px;
}

.phase-toggle .phase-toggle__btn {
    background: transparent !important;
    border: none !important;
    border-radius: 6px !important;
    color: rgba(255, 255, 255, 0.75) !important;
    font-family: Ubuntu, Arial, sans-serif;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: none;
    height: 34px !important;
}

.phase-toggle .phase-toggle__btn.v-btn--active {
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 50%, orange 100%) !important;
    color: #fff !important;
}

.phase-toggle .phase-toggle__btn.v-btn--active::before {
    opacity: 0 !important;
}

/* ---------- Round stepper ---------- */
.round-stepper {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 40px;
    padding: 0 4px;
    border: 1px solid rgba(0, 0, 0, 0.38);
    border-radius: 4px;
    font-family: Ubuntu, Arial, sans-serif;
    font-weight: 700;
}

.round-stepper__label {
    min-width: 150px;
    text-align: center;
}

.round-stepper--disabled {
    opacity: 0.5;
}

/* ---------- Mobile ---------- */
.filters-bar--mobile {
    padding: 12px 8px 0;
}

.filters-bar--mobile .filters-bar__inner {
    padding: 10px 12px;
    gap: 10px;
}

.filters-bar--mobile .filters-field--season,
.filters-bar--mobile .filters-field--league,
.filters-bar--mobile .filters-field--arena,
.filters-bar--mobile .filters-field--round {
    flex: 1 1 100%;
    min-width: 0;
    max-width: none;
}

.filters-bar--mobile .filters-field--phase {
    width: 100%;
}

.filters-bar--mobile .phase-toggle {
    display: flex;
    width: 100%;
}

.filters-bar--mobile .phase-toggle .phase-toggle__btn {
    flex: 1 1 0;
    min-width: 0 !important;
    padding: 0 6px !important;
    font-size: 0.75rem;
}

.filters-bar--mobile .round-stepper {
    display: flex;
    justify-content: space-between;
}
</style>
